import React, { useMemo } from 'react';
import type { DropzoneOptions } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components/atoms/badge';
import { DragAndDrop } from '@components/atoms/drag-and-drop';
import { Typography } from '@components/atoms/typography';
import { CheckMarkIcon } from '@components/icons/check-mark-icon';
import { DownloadIcon } from '@components/icons/download-icon';
import { ErrorMarkIcon } from '@components/icons/error-mark-icon';
import { LoadingFiles } from '@components/organisms/files-uploader/loading-files';
import { BadgeStatusEnum } from '@models/enums';
import type { FileWithPreview, ThemeProps } from '@models/types';
import type { CardProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/card/types';
import { RequirementsButton } from '@modules/dashboard/pages/docs-verification/docs-verification-page/docs-verification-steps/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/styles/useTheme';
import cn from 'classnames';

import { CardContainer } from './styles';

const MainCard: React.FC<{
    isDragDrop: boolean;
    children: React.ReactNode;
    onChange?: (file: FileWithPreview) => void;
    accept: DropzoneOptions['accept'];
}> = ({ isDragDrop, children, onChange, accept }) => {
    if (isDragDrop) {
        return (
            <DragAndDrop
                onChange={(files) => {
                    if (onChange && files?.[0]) {
                        onChange(files[0]);
                    }
                }}
                accept={accept}
                className='card-drag-drop'
            >
                {children}
            </DragAndDrop>
        );
    }

    return children as React.ReactElement;
};

export const Card: React.FC<CardProps> = ({
    previews,
    accept,
    onChangeFile,
    description,
    isLoading,
    isError,
    message,
    className,
    onRequirementsClick,
    icon,
    isSuccess,
    title,
}) => {
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const { t } = useTranslation();

    const actualBadgeText = useMemo(() => {
        if (message) {
            return t(message);
        }
        if (isError) {
            return t(message || 'common.pleaseTryAgainLater');
        }

        return '';
    }, [t, message, isError]);

    const actualStatus = useMemo(() => {
        if (isError) {
            return (
                <Badge
                    text={actualBadgeText}
                    status={BadgeStatusEnum.ERROR}
                    leftAddon={<ErrorMarkIcon />}
                    className='badge'
                />
            );
        }

        if (isSuccess && previews) {
            return (
                <Badge
                    text={actualBadgeText || t('common.uploadSuccessful')}
                    status={BadgeStatusEnum.SUCCESS}
                    leftAddon={<CheckMarkIcon />}
                    className='badge success-badge'
                />
            );
        }

        if (matchesMobileSm) {
            return <React.Fragment />;
        }

        if (onChangeFile && !previews) {
            return (
                <Typography
                    tag='div'
                    variant='small-text-bold'
                    color='text-light'
                    className='card-row'
                >
                    <DownloadIcon /> {t('filesUploader.dragAndDrop.text')}
                </Typography>
            );
        }

        return <React.Fragment />;
    }, [actualBadgeText, t, matchesMobileSm, isError, previews, isSuccess, onChangeFile]);

    const handleRequirementsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onRequirementsClick?.();
    };

    return (
        <CardContainer
            isDroppable={!!onChangeFile}
            className={cn(className, {
                white: isLoading || isError || !!previews,
            })}
        >
            <MainCard isDragDrop={!!onChangeFile} onChange={onChangeFile} accept={accept}>
                <React.Fragment>
                    {!isLoading && !isError && !previews && (
                        <Typography
                            tag='div'
                            variant='text-bold'
                            color='text-main'
                            className='card-title'
                        >
                            {title}
                        </Typography>
                    )}

                    {!isLoading && (
                        <div className='card-main'>
                            {actualStatus}
                            {isError && (
                                <RequirementsButton
                                    variant='ghost'
                                    onClick={handleRequirementsClick}
                                    className='requirements-button-error'
                                >
                                    {t('common.requirements')}
                                </RequirementsButton>
                            )}
                            <div className='card-wrapper'>
                                {previews && (
                                    <div className='card-images-container'>
                                        {previews.map((preview) => (
                                            <div className='card-img'>
                                                <img src={preview} alt='Preview' />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {!isError && !previews && (
                                    <div className='card-icon-container'>
                                        {typeof icon === 'string' ? (
                                            <img src={icon as string} alt='icon' className='img' />
                                        ) : (
                                            icon
                                        )}
                                    </div>
                                )}
                            </div>
                            {!previews && (
                                <React.Fragment>
                                    {!matchesMobileSm && !!description && !isError && (
                                        <Typography
                                            tag='div'
                                            variant='text-bold'
                                            color='text-light'
                                            className='card-description'
                                        >
                                            {description}
                                        </Typography>
                                    )}
                                    {!isError && (
                                        <RequirementsButton
                                            variant='ghost'
                                            onClick={handleRequirementsClick}
                                            className='requirements-button'
                                        >
                                            {t('common.requirements')}
                                        </RequirementsButton>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </React.Fragment>
            </MainCard>
            {isLoading && <LoadingFiles className='card-loading' />}
        </CardContainer>
    );
};
